import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { usePartnerProfile } from '@/partner/hooks/usePartnerProfile';
import { usePartnerBalance } from '@/partner/hooks/usePartnerDashboard';
import { Button } from '@medikei/ui';
import {
  Sparkles,
  Copy,
  Check,
  Share2,
  ArrowRight,
  Wallet,
  ShieldCheck,
} from 'lucide-react';

interface ReferralCardProps {
  className?: string;
  variant?: 'compact' | 'full';
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function ReferralCard({ className = '', variant = 'full' }: ReferralCardProps) {
  const { user } = useAuth();
  const { data: partner } = usePartnerProfile(!!user);
  const { data: balance } = usePartnerBalance(!!user);
  const [copied, setCopied] = useState(false);

  // Generate fallback hash from user email/id if still loading or not yet synchronized
  const partnerHash =
    partner?.hash ||
    (user?.email ? user.email.split('@')[0]?.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) : null) ||
    'MEDICINA';

  const getFullBaseUrl = () => {
    if (typeof window !== 'undefined' && window.location.origin) {
      if (window.location.origin.includes('localhost')) {
        return window.location.origin;
      }
      return 'https://www.medikei.com.br';
    }
    return 'https://www.medikei.com.br';
  };

  const referralLink = `${getFullBaseUrl()}/p/${partnerHash}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `Olá! Recomendo a plataforma Medikei para consultas médicas online ou presenciais. Use meu link exclusivo para agendar: ${referralLink}`
  )}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const availableBalance = balance?.available ?? 0;

  if (variant === 'compact') {
    return (
      <div
        className={`rounded-2xl p-5 bg-gradient-to-br from-[#0c2847] via-[#09223d] to-[#07192d] text-white border border-blue-800/40 shadow-xl relative overflow-hidden ${className}`}
      >
        <div className="absolute -top-12 -right-12 w-36 h-36 bg-emerald-500/20 rounded-full blur-2xl pointer-events-none" />

        <div className="flex items-center justify-between mb-3 relative z-10">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[11px] font-semibold uppercase tracking-wider">
            <Sparkles className="w-3 h-3 text-emerald-300" />
            <span>Indique e Ganhe</span>
          </div>
          <span className="text-xs font-bold text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded-full border border-amber-400/20">
            12% PIX
          </span>
        </div>

        <h3 className="font-bold text-base text-white mb-1.5 leading-snug">
          Ganhe no PIX indicando consultas
        </h3>
        <p className="text-xs text-slate-300/90 mb-4 leading-relaxed">
          Compartilhe seu link exclusivo. Receba 12% na 1ª consulta e 5% de recorrência por 6 meses.
        </p>

        <div className="flex items-center gap-2 mb-3">
          <input
            type="text"
            readOnly
            value={referralLink}
            className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-1.5 text-xs text-slate-200 font-mono select-all focus:outline-none"
          />
          <button
            onClick={handleCopy}
            className="p-2 rounded-xl bg-white/15 hover:bg-white/25 text-white transition-all text-xs font-semibold flex items-center justify-center shrink-0"
            title="Copiar link"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>

        <div className="pt-2 border-t border-white/10 flex items-center justify-between text-xs">
          <Link
            to="/parceiro"
            className="inline-flex items-center gap-1 font-semibold text-emerald-300 hover:text-emerald-200 transition-colors"
          >
            <span>Ver meus ganhos</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-slate-300 hover:text-white transition-colors"
          >
            <Share2 className="w-3.5 h-3.5 text-emerald-400" />
            <span>WhatsApp</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-7 bg-gradient-to-br from-[#0c2847] via-[#09223d] to-[#07192d] text-white border border-blue-800/40 shadow-2xl relative overflow-hidden ${className}`}
    >
      {/* Ambient glows */}
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2.5 sm:gap-3 mb-4 sm:mb-5 pb-3.5 sm:pb-4 border-b border-white/10">
          <div className="inline-flex items-center gap-2 px-2.5 sm:px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[11px] sm:text-xs font-semibold uppercase tracking-wider shadow-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
            <span>Programa Indique e Ganhe</span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-white/10 text-slate-200 font-bold text-[11px] sm:text-xs border border-white/15">
              12% 1ª consulta
            </span>
            <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-emerald-500/20 text-emerald-300 font-bold text-[11px] sm:text-xs border border-emerald-500/30">
              5% Recorrência 6m
            </span>
          </div>
        </div>

        {/* Title and Value Proposition */}
        <div className="grid lg:grid-cols-12 gap-4 sm:gap-6 items-center mb-4 sm:mb-6">
          <div className="lg:col-span-8 space-y-1.5 sm:space-y-2">
            <h2 className="text-lg sm:text-xl md:text-2xl font-extrabold text-white tracking-tight">
              Indique amigos e receba comissões no PIX.
            </h2>
            <p className="text-xs sm:text-sm text-slate-300/90 leading-relaxed max-w-xl">
              Compartilhe seu link exclusivo com amigos, familiares e contatos. Você recebe 12% da primeira consulta e 5% de todas as consultas realizadas nos próximos 6 meses.
            </p>
          </div>

          <div className="lg:col-span-4 bg-white/[0.08] backdrop-blur-md rounded-xl sm:rounded-2xl p-3.5 sm:p-4 border border-white/15">
            <div className="flex items-center justify-between text-xs text-slate-300 mb-1">
              <span className="flex items-center gap-1.5 font-medium">
                <Wallet className="w-3.5 h-3.5 text-emerald-400" />
                Saldo Disponível
              </span>
              <span className="text-[10px] text-emerald-400 font-bold uppercase">PIX Livre</span>
            </div>
            <div className="text-xl sm:text-2xl font-black text-white">
              {formatCurrency(availableBalance)}
            </div>
            <Link
              to="/parceiro/saques"
              className="text-xs text-emerald-300 hover:text-emerald-200 font-semibold mt-2 inline-flex items-center gap-1 transition-colors"
            >
              <span>Solicitar resgate</span>
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {/* Referral Link Copy Bar */}
        <div className="bg-black/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-white/10 space-y-2.5 sm:space-y-3 mb-4 sm:mb-6">
          <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">
            Seu Link Exclusivo de Indicação:
          </label>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="flex-1 bg-white/10 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 border border-white/15 text-xs sm:text-sm text-slate-100 font-mono select-all truncate">
              {referralLink}
            </div>

            <div className="grid grid-cols-2 sm:flex sm:items-center gap-2">
              <Button
                type="button"
                onClick={handleCopy}
                className="bg-white hover:bg-slate-100 text-[#0c2847] font-bold text-xs h-10 px-3.5 sm:px-4 rounded-xl flex items-center justify-center gap-1.5 shrink-0 shadow-md w-full sm:w-auto"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copiar Link</span>
                  </>
                )}
              </Button>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs h-10 px-3.5 sm:px-4 rounded-xl flex items-center justify-center gap-1.5 shrink-0 transition-colors shadow-md w-full sm:w-auto text-center"
              >
                <Share2 className="w-4 h-4" />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="pt-3.5 sm:pt-4 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2 text-[11px] sm:text-xs text-slate-300/80">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>Atribuição garantida por cookies de 30 dias • Saque mínimo de R$ 50,00</span>
          </div>

          <Link to="/parceiro" className="w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              className="bg-white/10 hover:bg-white/20 text-white border-white/25 rounded-xl font-semibold text-xs flex items-center justify-center gap-1.5 h-9 w-full sm:w-auto"
            >
              <span>Acessar Painel do Parceiro &amp; Relatórios</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
